/**
 * Auth Service - Email/Password Authentication
 * T016: Implement AuthService for email/password auth
 */

import type { GatewayClient } from './client'
import type {
  RegisterRequest,
  LoginRequest,
  LoginResult,
  VerifyEmailRequest,
  VerifyEmailResult,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ResendVerificationRequest,
  MessageResponse,
} from './types'

// ============================================================================
// Types
// ============================================================================

export interface AuthService {
  /**
   * Register a new user account
   */
  register(request: RegisterRequest): Promise<MessageResponse>

  /**
   * Login with email and password
   */
  login(request: LoginRequest): Promise<LoginResult>

  /**
   * Logout current session
   */
  logout(): Promise<void>

  /**
   * Verify email address with token
   */
  verifyEmail(request: VerifyEmailRequest): Promise<VerifyEmailResult>

  /**
   * Request password reset email
   */
  forgotPassword(request: ForgotPasswordRequest): Promise<MessageResponse>

  /**
   * Reset password with token
   */
  resetPassword(request: ResetPasswordRequest): Promise<MessageResponse>

  /**
   * Resend verification email
   */
  resendVerification(
    request: ResendVerificationRequest
  ): Promise<MessageResponse>
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create an AuthService instance
 */
export function createAuthService(client: GatewayClient): AuthService {
  return {
    async register(request: RegisterRequest): Promise<MessageResponse> {
      return client.post<MessageResponse>('/v1/auth/register', request)
    },

    async login(request: LoginRequest): Promise<LoginResult> {
      return client.post<LoginResult>('/v1/auth/login', request)
    },

    async logout(): Promise<void> {
      await client.post<MessageResponse>('/v1/auth/logout')
    },

    async verifyEmail(request: VerifyEmailRequest): Promise<VerifyEmailResult> {
      return client.post<VerifyEmailResult>('/v1/auth/verify-email', request)
    },

    async forgotPassword(
      request: ForgotPasswordRequest
    ): Promise<MessageResponse> {
      return client.post<MessageResponse>('/v1/auth/forgot-password', request)
    },

    async resetPassword(
      request: ResetPasswordRequest
    ): Promise<MessageResponse> {
      return client.post<MessageResponse>('/v1/auth/reset-password', request)
    },

    async resendVerification(
      request: ResendVerificationRequest
    ): Promise<MessageResponse> {
      return client.post<MessageResponse>(
        '/v1/auth/resend-verification',
        request
      )
    },
  }
}
